<template>
  <BaseLayout title="Homework List">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">List of Homeworks</h1>
      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <!-- F9: semester timeline; hidden when empty or when its side request failed -->
        <HomeworkTimeline
          v-if="timelineItems.length > 0"
          :items="timelineItems"
          :role="isTeacher ? 'teacher' : 'student'"
          :status-by-id="statusMap"
          :stats-by-id="statsMap"
        />
        <div v-if="listItems.length === 0" class="alert alert-info text-center">
          No homeworks available.
          <button @click="fetchHomeworks" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-2"></i> Refresh
          </button>
        </div>
        <div v-else class="list-group">
          <div v-for="item in listItems" :key="item.homework_id ?? item.id"
               class="list-group-item d-flex justify-content-between align-items-center"
               :class="{ 'hw-row--unreleased': !isTeacher && item.hw_status === 'unreleased' }"
               :title="!isTeacher && item.hw_status === 'unreleased' ? 'Homework not released yet' : null">
            <div>
              <h5>
                {{ item.title }}
                <!-- Student: server-computed badge -->
                <HomeworkStatusBadges
                  v-if="!isTeacher"
                  :hw-status="item.hw_status"
                  :in-queue="item.last_evaluation_in_queue"
                  :submitted-count="item.submitted_subtasks_count"
                  :total-required="item.total_required_subtasks"
                  :nearest-deadline="item.nearest_deadline"
                  :nearest-deadline-type="item.nearest_deadline_type"
                />
                <!-- Teacher: existing badge -->
                <template v-else>
                  <span :class="badgeMap[item.id].cls"
                        :aria-label="badgeMap[item.id].ariaLabel"
                        :title="badgeMap[item.id].tooltip">
                    {{ badgeMap[item.id].label }}
                  </span>
                  <small class="text-muted fw-normal ms-2">{{ badgeMap[item.id].tooltip }}</small>
                </template>
              </h5>
              <div v-html="renderMarkdown(item.description)" class="line-clamp"></div>
              <!-- Teacher stats row -->
              <div v-if="isTeacher && statsMap[item.id]" class="small text-muted mt-1">
                <i class="bi bi-people me-1"></i>
                Submitted: {{ statsMap[item.id].students_submitted_count }}/{{ statsMap[item.id].students_total_count }}
                &nbsp;·&nbsp;
                Evaluated: {{ statsMap[item.id].students_evaluated_count }}
                &nbsp;·&nbsp;
                Passed: {{ statsMap[item.id].students_passed_count }}
              </div>
            </div>
            <div class="d-flex">
              <router-link v-if="isTeacher || item.hw_status !== 'unreleased'" :to="`/homework/${item.homework_id ?? item.id}`" class="btn btn-primary btn mx-1 d-flex align-items-center">
                <i class="bi bi-eye me-2"></i>
                <span>View</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link v-if="isTeacher" to="/homework/create" class="btn btn-primary mb-2">
          <i class="bi bi-plus-circle"></i> Create New Homework
        </router-link>
      </div>
      <div v-if="!isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Dataset:</h5>
        <button @click="downloadDatasetForStudent" class="btn btn-success" :disabled="isDownloading" v-tooltip="'This action can take a while.'">
          <span v-if="isDownloading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <i class="bi bi-download"></i> Download Dataset
        </button>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal"/>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import HomeworkController from '@/controllers/HomeworkController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import HomeworkStatusBadges from '@/components/HomeworkStatusBadges.vue';
import HomeworkTimeline from '@/components/HomeworkTimeline.vue';
import DatasetController from '@/controllers/DatasetController';
import { renderMarkdown } from '@/utils/renderMarkdown';
import { mapGetters } from 'vuex';

export default {
  name: 'HomeworkListView',
  components: {
    BaseLayout,
    StemErrorModal,
    HomeworkStatusBadges,
    HomeworkTimeline,
  },
  data() {
    return {
      homeworks: [],
      homeworkStatuses: [],
      stats: [],
      timelineItems: [],
      loading: true,
      isDownloading: false,
    };
  },
  watch: {
    isTeacher() {
      this.fetchHomeworks();
    },
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId', 'semesterId']),
    listItems() {
      return this.isTeacher ? this.homeworks : this.homeworkStatuses;
    },
    statusMap() {
      const map = {};
      for (const s of this.homeworkStatuses) map[s.homework_id] = s.hw_status;
      return map;
    },
    statsMap() {
      const map = {};
      for (const s of this.stats) map[s.homework_id] = s;
      return map;
    },
    badgeMap() {
      const map = {};
      for (const hw of this.homeworks) {
        map[hw.id] = this.teacherBadge(hw);
      }
      return map;
    },
  },
  async created() {
    try {
      await this.loadAll();
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    renderMarkdown,
    async loadAll() {
      this.loadTimeline();   // side request: a failure must not block the list (E12)
      if (this.isTeacher) {
        this.homeworks = await HomeworkController.fetchHomeworks(true);
        HomeworkController.fetchHomeworkStatsForTeacher(this.semesterId)
          .then(stats => { this.stats = stats; })
          .catch(() => { this.stats = []; });
      } else {
        this.homeworkStatuses = await HomeworkController.fetchHomeworkStatusList();
      }
    },
    loadTimeline() {
      this.timelineItems = [];
      HomeworkController.fetchTimelineData()
        .then(items => { this.timelineItems = items; })
        .catch(() => { this.timelineItems = []; });
    },
    async fetchHomeworks() {
      this.loading = true;
      try {
        await this.loadAll();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    teacherBadge(hw) {
      const now = new Date();
      if (hw.archived)
        return { cls: 'badge bg-secondary ms-2', label: 'Archived', ariaLabel: 'Archived', tooltip: 'Homework from a previous semester' };
      if (new Date(hw.release_date) > now)
        return { cls: 'badge bg-primary ms-2', label: 'Unreleased', ariaLabel: 'Unreleased', tooltip: `Releases on ${new Date(hw.release_date).toLocaleDateString()}` };
      const dd = hw.deadline_dates || {};
      const lastDeadline = [dd.pre, dd.submission, dd.post, dd.manual]
        .filter(Boolean).map(d => new Date(d)).sort((a, b) => b - a)[0];
      if (lastDeadline && now > lastDeadline)
        return { cls: 'badge badge-purple ms-2', label: 'Ended', ariaLabel: 'Ended', tooltip: 'All deadlines have passed' };
      return { cls: 'badge bg-success ms-2', label: 'Active', ariaLabel: 'Active', tooltip: 'Homework is published and ongoing' };
    },
    async downloadDatasetForStudent() {
      // only for students
      try {
        this.isDownloading = true;
        if (!this.isTeacher) {
          const studentId = this.userId;
          const dataset = await DatasetController.getDatasetForStudent(studentId);
          if (dataset && dataset.file_path) {
            await this.downloadFile(dataset.file_path);
          }
          else {
            this.$store.dispatch('triggerAlert', {
              title: 'Error',
              message: 'No dataset found. Contact teacher, please.',
              type: 'error',
            });
          }
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDownloading = false;
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await HomeworkController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
  },
};
</script>

<!-- Max 3 line of description to display -->
<style scoped>
  .line-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* F9: unreleased row; not pointer-events: none, that would block the native tooltip */
  .hw-row--unreleased {
    opacity: 0.55;
    cursor: not-allowed;
    user-select: none;
  }
  .badge-purple {
    background-color: #6f42c1;
    color: #fff;
  }
</style>